/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable arrow-body-style */
import { create } from 'zustand';
import { routeObserver } from '@src/common/util/routeObserver';
import { LinkAnalysisFilterPanelStore, LaFilterPanelState } from '../types';
import { laFetchFilters } from '../api/laFetchFilters';
import { laFilterPanelChangeManager } from './laFilterPanelChangeManager';
import { linkAnalysisResultsApi } from './linkAnalysisResults.state';
import { DEFAULT_HOP_VALUE } from '../components/filterPanelComponents/defaultHopValue';
import { getValidatedConnectionsValue } from './getValidatedConnectionsValue';

export const useLinkAnalysisFilterPanelStore =
  create<LinkAnalysisFilterPanelStore>(() => ({
    nodeTypes: [],
    nodeOptions: [],
    nodesSelected: [],
    idTypeOptions: [],
    idTypeSelected: '',
    idTypeValidationMsg: '',
    value: '',
    maxIpConnections: '',
    hop: DEFAULT_HOP_VALUE,
    valueValidationMsg: '',
    searchBtnEnabled: false,
    loading: true,
  }));
const store = useLinkAnalysisFilterPanelStore;
export const linkAnalysisFilterPanelApi = api();

function api() {
  let hasContext = false;
  routeObserver.observeLinkAnalysisEnter(init);

  return {
    init,
    setNodesValue,
    setTypeValue: setIdTypeValue,
    setValue,
    setHopValue,
    setMaxConnectionsValue,
    incrementMaxConnectionsValue,
    decrementMaxConnectionsValue,
    submit,
    submitKbd,
  };

  async function init() {
    if (hasContext) {
      laFilterPanelChangeManager.setSubmitBlocked(false);
      commitState();
      enableSearchBlockObserver();
      return;
    }

    store.setState({ loading: true });
    const {
      payload: {
        idTypeSelectOptions: idTypeOptions,
        nodeTypeSelectOptions: nodeOptions,
        nodeTypes,
      },
    } = await laFetchFilters();

    hasContext = idTypeOptions.length > 0 && nodeOptions.length > 0;
    store.setState({
      idTypeOptions,
      nodeTypes,
      nodeOptions,
      nodesSelected: nodeOptions.map((e) => e.value),
      loading: false,
    });
    laFilterPanelChangeManager.setSubmitBlocked(false);
    commitState();
    enableSearchBlockObserver();
  }

  async function submitKbd(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      await submit();
    }
  }

  async function submit() {
    const valid = validate();
    if (!valid) {
      return;
    }
    if (!laFilterPanelChangeManager.isSubmittable(getFilterPanelState())) {
      return;
    }

    store.setState({
      searchBtnEnabled: false,
    });
    const fpState = commitState();
    await linkAnalysisResultsApi.search(fpState);
  }

  function setNodesValue(value: string[]) {
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      nodesValue: value,
    });

    store.setState({
      nodesSelected: value,
      searchBtnEnabled,
    });
  }

  function setIdTypeValue(value: string) {
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      idType: value,
    });

    store.setState({
      idTypeSelected: value,
      idTypeValidationMsg: '',
      valueValidationMsg: '',
      searchBtnEnabled,
    });
  }

  function setHopValue(value: string) {
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      hop: value,
    });
    store.setState({
      hop: value,
      searchBtnEnabled,
    });
  }

  function setValue(event: React.ChangeEvent<HTMLInputElement>) {
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      value: event.target.value,
    });

    const isAlreadyRed = !!store.getState().valueValidationMsg;

    store.setState({
      value: event.target.value,
      valueValidationMsg: isAlreadyRed
        ? validateIdValue(event.target.value)
        : '',
      searchBtnEnabled,
    });
  }

  function setMaxConnectionsValue(event: React.ChangeEvent<HTMLInputElement>) {
    const { currentValue: maxIpConnections } = getValidatedConnectionsValue(
      event.target.value
    );
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      maxIpConnections,
    });

    store.setState({
      maxIpConnections,
      searchBtnEnabled,
    });
  }

  function incrementMaxConnectionsValue() {
    const { nextValue: maxIpConnections } = getValidatedConnectionsValue(
      store.getState().maxIpConnections
    );
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      maxIpConnections,
    });

    store.setState({
      maxIpConnections,
      searchBtnEnabled,
    });
  }
  function decrementMaxConnectionsValue() {
    const { prevValue: maxIpConnections } = getValidatedConnectionsValue(
      store.getState().maxIpConnections
    );
    const searchBtnEnabled = laFilterPanelChangeManager.isSubmittable({
      ...getFilterPanelState(),
      maxIpConnections,
    });

    store.setState({
      maxIpConnections,
      searchBtnEnabled,
    });
  }

  function validate() {
    const { idTypeSelected, value } = store.getState();
    const valueValidationMsg = validateIdValue(value);
    const validValue = !valueValidationMsg;
    const validTypeSelected = idTypeSelected.trim() !== '';
    const idTypeValidationMsg = validTypeSelected ? '' : 'Select a value';
    store.setState({
      idTypeValidationMsg,
      valueValidationMsg,
    });
    return validValue && validTypeSelected;
  }

  function commitState() {
    const state = getFilterPanelState();
    laFilterPanelChangeManager.commitSearchProps(state);
    return state;
  }

  function getFilterPanelState(): LaFilterPanelState {
    const {
      idTypeSelected: idType,
      value,
      nodesSelected: nodesValue,
      hop,
      maxIpConnections,
    } = store.getState();
    return {
      nodesValue,
      idType,
      value,
      hop,
      maxIpConnections,
    };
  }

  function validateIdValue(value: string) {
    return value.trim().length >= 3 ? '' : 'Minimum 3 characters required';
  }

  function enableSearchBlockObserver() {
    laFilterPanelChangeManager.setOnSubmitBlocked(() =>
      store.setState({
        searchBtnEnabled: laFilterPanelChangeManager.isSubmittable(
          getFilterPanelState()
        ),
      })
    );
  }
}
